<script setup lang="ts">
import { useMask } from 'core/services/mask'

defineProps<{
  isMenuOpen: boolean
  items: any[]
}>()

const emit = defineEmits(['update:isMenuOpen'])

const { phone } = useMask()
const isOpen = ref(false)
const isError = ref(false)
const view = ref('default')

function openModal() {
  isOpen.value = true
  view.value = 'default'
  isError.value = false
}

async function handleContactMe(val: { contactBoxPhone: string }) {
  const data = await fetchWebApi('/einsurance/contact-me', {
    method: 'GET',
    query: {
      phone: val.contactBoxPhone,
      urlReferrer: window.location.href,
      isEnglish: false,
    },
  })

  isError.value = !data?.submitted || true
  view.value = data?.submitted ? 'success' : 'default'
}
</script>

<template>
  <div>
    <div class="max-w-[1260px] mx-auto flex px-2 font-poppins xl:px-0 gap-2 items-center h-15 xl:h-20">
      <BaseModal v-model="isOpen">
        <template #content>
          <div class="flex flex-col p-15">
            <div v-if="view === 'default'">
              <h2 class="text-2xl max-w-2xl font-bold text-primary">
                Zákaznická infolinka
              </h2>
              <div class="py-2">
                <span class="font-bold">
                  Po-Pá
                </span>
                9:00-19:30
                <span class="font-bold">
                  So-Ne
                </span>
                14:00-18:00
              </div>
              <h2 class="text-2xl max-w-2xl font-bold text-primary">
                Zavoláme Vám zdarma zpět!
              </h2>
              <FormSimple @submit="handleContactMe">
                <div class="flex items-center mt-3">
                  <div class="flex">
                    <FormFieldText class="flex items-center" name="contactBoxPhone" placeholder="tel. číslo" :mask="phone" />
                  </div>
                  <BaseButton submit text="ODESLAT >" class="h-47px -ml-4 whitespace-nowrap" size="normal" />
                </div>
              </FormSimple>
              <div v-if="isError">
                Prosím zadejte validní telefonní číslo
              </div>
            </div>

            <div v-if="view === 'success'">
              <h2 class="text-3xl text-center max-w-2xl font-bold text-primary pb-2">
                Odesláno
              </h2>
              <p>
                Děkujeme za Váš požadavek a kontaktní telefon.<br>
                V nejbližší chvíli se Vám ozve náš opetátor<br>
                a zodpoví veškeré Vaše dotazy.
              </p>
            </div>
          </div>
        </template>
      </BaseModal>
      <NuxtLink to="/" @click="emit('update:isMenuOpen', false)">
        <img class="w-130px lg:w-180px" src="@/assets/logo.png" alt="epojisteni logo">
      </NuxtLink>
      <div class="flex py-auto gap-4 sm:gap-8 items-center ml-auto flex-row">
        <div class="flex items-center gap-2">
          <a href="tel:800 44 44 33">
            <AppIcon size="24" icon="fal-phone-alt" class="text-gray-special-light" />
          </a>
          <span class="font-bold text-primary hidden lg:inline">800 44 44 33</span>
          <BaseButton
            size="small" text="Zavolejte mi" render="special" class="hidden ml-2 sm:inline bg-secondary-active text-white"
            @click.prevent="openModal"
          />
        </div>
        <div class="lg:px-8 lg:border-x-1">
          <a href="mailto:info@epojisteni.cz" class="flex gap-2 text-secondary-active hover:underline">
            <AppIcon size="24" icon="fal-envelope" class="text-gray-special-light" />
            <span class="text-normal hidden xl:inline">info@epojisteni.cz</span>
          </a>
        </div>
        <div class="lg:pr-8 lg:border-r-1">
          <RouterLink to="/aktuality/" class="flex gap-2 text-secondary-active hover:underline" active-class="blog-link-active">
            <AppIcon size="24" icon="fal-newspaper" class="text-gray-special-light" />
            <span class="text-normal hidden md:inline">Blog</span>
          </RouterLink>
        </div>
        <div>
          <a href="https://www.epojisteni.cz/klient/prihlaseni/" class="flex gap-2 text-secondary-active hover:underline">
            <AppIcon size="24" icon="fal-user" class="text-gray-special-light" />
            <span class="whitespace-nowrap text-normal hidden md:inline">Klientská sekce</span>
          </a>
        </div>
      </div>
      <TheNavigationHamburger
        class="text-gray-special-light lg:hidden" :open="isMenuOpen"
        @click="$emit('update:isMenuOpen', !isMenuOpen)"
      />
    </div>

    <nav class="bg-gray-special text-white font-poppins <lg:hidden xl:flex">
      <div class="max-w-[1288px] mx-auto flex items-center h-12 xl:h-14">
        <NuxtLink class="flex items-center <lg:hidden h-full p-5" to="/" @click="emit('update:isMenuOpen', false)">
          <AppIcon size="20" icon="fal-home-alt" class="lg:hover:text-primary" />
        </NuxtLink>
        <div class="hidden lg:flex lg:py-0 lg:h-full" :class="{ isMenuOpen }">
          <TheNavigationMenu :items="items" @is-menu-open="emit('update:isMenuOpen', false)" />
        </div>
      </div>
    </nav>

    <nav class="bg-gray-special text-white font-poppins hidden" :class="{ isMenuOpen }">
      <TheNavigationMenu :items="items" @is-menu-open="emit('update:isMenuOpen', false)" />
    </nav>
  </div>
</template>

<style lang="scss" scoped>
.isMenuOpen {
  @screen <lg {
    @apply left-0 top-[60px] absolute flex flex-col z-50 bg-gray-special w-full
  }
}

.router-link-active {
  @apply text-white bg-primary pointer-events-none
}
</style>
